import { useEffect, useMemo, useState } from 'react'
import { X } from 'lucide-react'
import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'

export interface UkWeeklyPlanRow {
  week_id: number
  period_id: number
  week_number_in_period: number
  week_start: string
  week_end: string
  week_len_days: number
  plan_revenue_rub: number | null
  created_at: string | null
  created_by_user_id: string | null
  updated_at: string | null
  updated_by_user_id: string | null
}

interface Props {
  isOpen: boolean
  onClose: () => void
  periodId: number | null
  weeks: UkWeeklyPlanRow[]
  userId?: string | null
  onSaved?: () => Promise<void> | void
}

type WeekState = 'past' | 'current' | 'future'

function getTodayIso() {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function formatShortDate(date: string) {
  const [year, month, day] = date.split('-')
  return `${day}.${month}.${year.slice(-2)}`
}

function formatNumberWithSpaces(value: number | undefined) {
  if (value === undefined || !Number.isFinite(value)) return ''
  return Math.round(value).toLocaleString('ru-RU')
}

function parseFormattedNumber(value: string) {
  const digitsOnly = value.replace(/[^\d]/g, '')
  if (!digitsOnly) return undefined
  return Number(digitsOnly)
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) {
    return error.message
  }

  if (error && typeof error === 'object') {
    const maybeMessage = 'message' in error ? error.message : null
    const maybeDetails = 'details' in error ? error.details : null
    const maybeHint = 'hint' in error ? error.hint : null

    const parts = [maybeMessage, maybeDetails, maybeHint]
      .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)

    if (parts.length > 0) {
      return parts.join(' ')
    }
  }

  return 'Ошибка сохранения плана УК'
}

export function UkWeeklyPlanModal({
  isOpen,
  onClose,
  periodId,
  weeks,
  userId,
  onSaved
}: Props) {
  const [plansByWeek, setPlansByWeek] = useState<Record<number, number | undefined>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const todayIso = useMemo(() => getTodayIso(), [])

  useEffect(() => {
    if (!isOpen) return

    const nextPlans: Record<number, number | undefined> = {}
    weeks.forEach((week) => {
      nextPlans[week.week_id] = week.plan_revenue_rub === null ? undefined : Number(week.plan_revenue_rub)
    })
    setPlansByWeek(nextPlans)
    setError(null)
  }, [isOpen, weeks])

  if (!isOpen) return null

  const getWeekState = (week: UkWeeklyPlanRow): WeekState => {
    if (week.week_end < todayIso) return 'past'
    if (week.week_start > todayIso) return 'future'
    return 'current'
  }

  const handleSave = async () => {
    if (!periodId || !userId) {
      setError('Недостаточно данных для сохранения')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const editableWeeks = weeks.filter((week) => getWeekState(week) !== 'past')
      const payload = editableWeeks.map((week) => ({
        week_id: week.week_id,
        plan_revenue_rub: plansByWeek[week.week_id] ?? null
      }))

      const supabase = getSupabase()
      const { error: saveError } = await withAuthRecovery(() =>
        (supabase as any).rpc('save_uk_weekly_plan', {
          p_period_id: periodId,
          p_items: payload
        })
      )

      if (saveError) {
        throw saveError
      }

      await onSaved?.()
      onClose()
    } catch (err) {
      console.error('Ошибка сохранения плана УК:', err)
      setError(getErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-card shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">План УК по неделям</h2>
            <p className="mt-1 text-sm text-slate-500">
              Прошедшие недели доступны только для просмотра.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
            aria-label="Закрыть"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[calc(90vh-152px)] overflow-y-auto px-5 py-4">
          <div className="space-y-3">
            {weeks.map((week) => {
              const state = getWeekState(week)
              const isPast = state === 'past'

              return (
                <div
                  key={week.week_id}
                  className={`rounded-2xl border px-4 py-4 ${
                    isPast ? 'border-slate-200 bg-slate-50' : 'border-slate-200 bg-card'
                  }`}
                >
                  <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_220px]">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold text-slate-950">
                          Неделя {week.week_number_in_period}
                        </h3>
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                            state === 'past'
                              ? 'bg-slate-200 text-slate-600'
                              : state === 'current'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-emerald-100 text-emerald-700'
                          }`}
                        >
                          {state === 'past' ? 'Завершена' : state === 'current' ? 'Текущая' : 'Будущая'}
                        </span>
                      </div>

                      <div className="text-sm text-slate-600">
                        {formatShortDate(week.week_start)} - {formatShortDate(week.week_end)}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={formatNumberWithSpaces(plansByWeek[week.week_id])}
                        onChange={(event) => {
                          const value = parseFormattedNumber(event.target.value)
                          setPlansByWeek((prev) => ({
                            ...prev,
                            [week.week_id]: value
                          }))
                        }}
                        placeholder="Не задан"
                        aria-label={`План недели ${week.week_number_in_period} в рублях`}
                        disabled={isPast || saving}
                        className="input w-full"
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {error && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            disabled={saving}
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="rounded-lg bg-foreground px-4 py-2 text-sm font-medium text-background transition-colors hover:bg-foreground/90 disabled:opacity-50"
            disabled={saving}
          >
            {saving ? 'Сохраняем...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  )
}
